const values = value => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.value)) return value.value;
  return [];
};

const key = value => String(value ?? "").trim().toLowerCase();

export function playlistReferences(assetId, playlists) {
  const wanted = key(assetId);
  if (!wanted) return [];
  const found = [];
  const seen = new Set();
  for (const playlist of values(playlists)) {
    if (!values(playlist?.items).some(item => key(item?.assetId ?? item?.id) === wanted || key(item?.audio?.assetId)===wanted ||
      values(item?.interactions?.touchAreas).some(area=>key(area?.audio?.assetId)===wanted || key(area?.action?.targetContentId)===wanted))) continue;
    const id = String(playlist.id ?? playlist.playlistId ?? "");
    if (!id || seen.has(key(id))) continue;
    seen.add(key(id));
    found.push({ id, name: String(playlist.name ?? "Saved playlist") });
  }
  return found;
}

export function validateDisplayCommand(command, expected) {
  const requestId = key(expected?.requestId);
  const expectedIds = [...new Set(values(expected?.deviceIds).map(key).filter(Boolean))];
  const targets = values(command?.targets);
  const commandStates = new Set(["Pending", "Acknowledged", "Superseded", "Completed"]);
  const targetStates = new Set(["Pending", "Acknowledged", "Superseded"]);
  if (!requestId || Number(command?.contractVersion) !== 2 || key(command?.requestId) !== requestId
      || key(command?.commandId) !== requestId || command?.displayOn !== expected?.displayOn
      || !commandStates.has(command?.state))
    throw new Error("Pirdy could not verify this display request. Refresh before trying again.");
  if (!expectedIds.length || targets.length !== expectedIds.length)
    throw new Error("Pirdy did not confirm every selected display. Refresh before trying again.");
  const byId = new Map();
  for (const target of targets) {
    const id = key(target?.deviceId);
    const version = Number(target?.displayControlVersion);
    if (!id || byId.has(id) || target?.desiredDisplayOn !== expected.displayOn || !Number.isSafeInteger(version)
        || version <= 0 || !targetStates.has(target?.state))
      throw new Error("Pirdy returned an invalid display confirmation. Refresh before trying again.");
    const acknowledged = target.state === "Acknowledged";
    const exactAck = Number(target?.acknowledgedDisplayControlVersion) === version
      && target?.reportedDisplayOn === target?.desiredDisplayOn && Boolean(target?.acknowledgedAtUtc);
    if (acknowledged !== exactAck)
      throw new Error("Pirdy returned an inconsistent display acknowledgement. Refresh before trying again.");
    byId.set(id, target);
  }
  if (expectedIds.some(id => !byId.has(id)))
    throw new Error("Pirdy did not confirm every selected display. Refresh before trying again.");
  const ordered = expectedIds.map(id => byId.get(id));
  const confirmed = ordered.filter(target => target.state === "Acknowledged").length;
  const immediate = ordered.filter(target => target.wakeAccepted === true).length;
  const deferred = ordered.filter(target => target.state === "Pending" && target.wakeAccepted !== true).length;
  if (Number(command?.acknowledged) !== confirmed || Number(command?.immediate) !== immediate
      || Number(command?.deferred) !== deferred)
    throw new Error("Pirdy returned inconsistent display-command totals. Refresh before trying again.");
  return ordered;
}

export function displayAcknowledgement(targets) {
  const result = { confirmed: 0, awaiting: 0, queued: 0, superseded: 0, total: values(targets).length };
  for (const target of values(targets)) {
    if (target.state === "Acknowledged") result.confirmed++;
    else if (target.state === "Superseded") result.superseded++;
    else if (target.wakeAccepted === true) result.awaiting++;
    else result.queued++;
  }
  return result;
}

export function applyDisplayCommandStatuses(statuses, targets) {
  const byId = new Map(values(statuses).map(status => [key(status?.deviceId), { ...status }]));
  for (const target of values(targets)) {
    const id = key(target?.deviceId);
    const version = Number(target?.displayControlVersion);
    const current = byId.get(id);
    if (current && Number(current.displayControlVersion ?? 0) > version) continue;
    const next = {
      ...(current ?? { deviceId: target.deviceId, activePublicationVersion: 0, playbackState: "NotReported" }),
      desiredPublicationVersion: target.desiredPublicationVersion,
      desiredDisplayOn: target.desiredDisplayOn,
      displayControlVersion: version,
    };
    if (target.state === "Acknowledged") {
      next.acknowledgedDisplayControlVersion = target.acknowledgedDisplayControlVersion;
      next.reportedDisplayOn = target.reportedDisplayOn;
    }
    byId.set(id, next);
  }
  return [...byId.values()];
}

export const supportCategories = Object.freeze([
  "DisplayControl",
  "ContentAndPlaylists",
  "UsbAndLocal",
  "WirelessRetrieval",
  "CloudAndSync",
  "BillingAndSubscription",
  "AccountAndSignIn",
  "FirmwareAndUpdates",
  "Other",
]);

export function supportTicketPayload(input) {
  const category = String(input?.category ?? "").trim();
  const other = String(input?.otherCategory ?? "").trim();
  const subject = String(input?.subject ?? "").trim();
  const description = String(input?.description ?? "").trim();
  if (!supportCategories.includes(category)) throw new Error("Choose an issue type.");
  if (category === "Other") {
    if (!other) throw new Error("Describe the issue type.");
    if (other.length > 80) throw new Error("Keep the issue type under 80 characters.");
    if (!subject || subject.length > 160) throw new Error("Enter a subject using up to 160 characters.");
    if (description.length < 20 || description.length > 4000)
      throw new Error("Describe what happened using 20 to 4,000 characters.");
    return { category, otherCategory: other, subject, description, source: "Web" };
  }
  if (!subject || subject.length > 160) throw new Error("Enter a subject using up to 160 characters.");
  if (description.length < 20 || description.length > 4000)
    throw new Error("Describe what happened using 20 to 4,000 characters.");
  return { category, subject, description, source: "Web" };
}
